import React, { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { Upload, FileSpreadsheet, FileText, CheckCircle, AlertCircle, ArrowRight, ArrowLeft, Loader2, Info, ShoppingCart, Package } from 'lucide-react';
import api from '../api';

export default function FileUpload() {
  const [step, setStep] = useState(1);
  const [fileType, setFileType] = useState('compras');
  const [uploadResult, setUploadResult] = useState(null);
  const [columnMapping, setColumnMapping] = useState({});
  const [processResult, setProcessResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const autoDetectMapping = (columns) => {
    const mapping = {};
    for (const col of columns) {
      const lower = col.toLowerCase();
      if (['producto', 'articulo', 'descripcion', 'product', 'item', 'nombre'].some(k => lower.includes(k))) mapping.producto = col;
      if (['cantidad', 'qty', 'pzas', 'unidades', 'quantity', 'stock'].some(k => lower.includes(k))) mapping.cantidad = col;
      if (['fecha', 'date', 'emisión', 'emision', 'periodo'].some(k => lower.includes(k))) mapping.fecha = col;
    }
    return mapping;
  };

  const onDrop = useCallback(async (acceptedFiles) => {
    if (acceptedFiles.length === 0) return;
    setLoading(true);
    setError('');
    try {
      const result = await api.upload(acceptedFiles[0], fileType);
      setUploadResult(result);
      const mapping = autoDetectMapping(result.columns || []);
      setColumnMapping(mapping);
      setStep(2);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [fileType]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'text/csv': ['.csv'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/vnd.ms-excel': ['.xls'],
      'application/pdf': ['.pdf'],
    },
    maxFiles: 1,
  });

  const handleProcess = async () => {
    if (!uploadResult?.upload_id) return;
    setLoading(true);
    setError('');
    try {
      const result = await api.processUpload(uploadResult.upload_id, { column_mapping: columnMapping });
      setProcessResult(result);
      setStep(3);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const FILE_TYPES = [
    { 
      value: 'compras', 
      label: 'Facturas / Compras', 
      icon: ShoppingCart,
      description: 'Sube facturas de proveedores (PDF, Excel o CSV). Los productos se homologarán contra tu catálogo maestro.',
      color: 'hsl(199,84%,38%)',
    },
    { 
      value: 'inventario', 
      label: 'Inventario Diario', 
      icon: Package,
      description: 'Sube el inventario actual (después de las 10am). Este REEMPLAZARÁ el inventario anterior.',
      color: 'hsl(152,63%,33%)',
    },
  ];

  const selectedType = FILE_TYPES.find(t => t.value === fileType);

  return (
    <div>
      {/* Steps */}
      <div className="steps">
        {[
          { num: 1, label: 'Seleccionar archivo' },
          { num: 2, label: 'Confirmar datos' },
          { num: 3, label: 'Resultados' },
        ].map((s, i, arr) => (
          <React.Fragment key={s.num}>
            <div className={`step ${step === s.num ? 'active' : ''} ${step > s.num ? 'completed' : ''}`}>
              <div className="step-number">{step > s.num ? '✓' : s.num}</div>
              <span className="step-label">{s.label}</span>
            </div>
            {i < arr.length - 1 && <div className={`step-line ${step > s.num ? 'completed' : ''}`} style={{flex:'0 0 60px'}} />}
          </React.Fragment>
        ))}
      </div>

      {error && (
        <div style={{ background: 'hsl(0 72% 48% / 0.08)', color: 'hsl(0 72% 40%)', padding: '12px 16px', borderRadius: '8px', fontSize: '0.82rem', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <AlertCircle size={16} /> {error}
        </div>
      )}

      {/* Step 1: File Selection */}
      {step === 1 && (
        <div>
          <div className="section-card" style={{marginBottom:'16px'}}>
            <h3>¿Qué estás cargando?</h3>
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'12px',marginTop:'12px'}}>
              {FILE_TYPES.map(t => {
                const Icon = t.icon;
                const isSelected = fileType === t.value;
                return (
                  <div 
                    key={t.value}
                    onClick={() => setFileType(t.value)}
                    style={{
                      padding: '16px',
                      borderRadius: '10px',
                      border: `2px solid ${isSelected ? t.color : 'hsl(var(--border))'}`,
                      background: isSelected ? `${t.color}08` : 'hsl(var(--card))',
                      cursor: 'pointer',
                      transition: 'border-color 0.15s',
                    }}
                  >
                    <div style={{display:'flex',alignItems:'center',gap:'8px',marginBottom:'6px'}}>
                      <Icon size={18} style={{color: t.color}} />
                      <span style={{fontWeight:600,fontSize:'0.9rem'}}>{t.label}</span>
                    </div>
                    <p style={{fontSize:'0.78rem',color:'hsl(var(--muted-foreground))',lineHeight:'1.5',margin:0}}>
                      {t.description}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>

          <div {...getRootProps()} className={`upload-zone ${isDragActive ? 'active' : ''}`} data-testid="upload-wizard-file-input">
            <input {...getInputProps()} />
            <Upload size={40} />
            <h3 style={{marginBottom:'4px'}}>Arrastra tu archivo aquí</h3>
            <p style={{color:'hsl(var(--muted-foreground))',fontSize:'0.82rem'}}>
              {fileType === 'compras' 
                ? 'Factura del proveedor en PDF, Excel o CSV' 
                : 'Reporte de inventario en Excel o CSV'}
            </p>
            <div style={{display:'flex',gap:'16px',justifyContent:'center',marginTop:'12px'}}>
              <span style={{display:'inline-flex',alignItems:'center',gap:'4px',fontSize:'0.72rem',color:'hsl(var(--muted-foreground))',background:'hsl(var(--muted)/0.5)',padding:'4px 10px',borderRadius:'6px'}}><FileSpreadsheet size={12} /> Excel/CSV</span>
              <span style={{display:'inline-flex',alignItems:'center',gap:'4px',fontSize:'0.72rem',color:'hsl(var(--muted-foreground))',background:'hsl(var(--muted)/0.5)',padding:'4px 10px',borderRadius:'6px'}}><FileText size={12} /> PDF</span>
            </div>
            {loading && <div style={{marginTop:'12px'}}><div className="spinner" style={{margin:'0 auto'}} /></div>}
          </div>
        </div>
      )}

      {/* Step 2: Preview + Confirm */}
      {step === 2 && uploadResult && (
        <div>
          {uploadResult.pdf_notice && (
            <div style={{ background: 'hsl(199 84% 38% / 0.08)', color: 'hsl(199 84% 30%)', padding: '12px 16px', borderRadius: '8px', fontSize: '0.82rem', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Info size={16} /> {uploadResult.pdf_notice}
            </div>
          )}

          {fileType === 'inventario' && (
            <div style={{background:'hsl(35 92% 45% / 0.08)',padding:'12px 16px',borderRadius:'8px',marginBottom:'16px',fontSize:'0.82rem',color:'hsl(35 80% 30%)',display:'flex',alignItems:'center',gap:'8px'}}>
              <AlertCircle size={16} />
              Este inventario REEMPLAZARÁ el anterior. Las ventas se calcularán automáticamente por diferencia.
            </div>
          )}

          <div className="section-card">
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:'12px'}}>
              <div>
                <h3>
                  {uploadResult.source_format === 'pdf' ? <FileText size={16} style={{display:'inline',marginRight:'6px'}} /> : <FileSpreadsheet size={16} style={{display:'inline',marginRight:'6px'}} />}
                  {uploadResult.filename}
                </h3>
                <p style={{fontSize:'0.8rem',color:'hsl(var(--muted-foreground))',marginTop:'2px'}}>
                  {uploadResult.rows} filas detectadas
                  {uploadResult.source_format === 'pdf' && <span style={{marginLeft:'8px',background:'hsl(199 84% 38% / 0.1)',color:'hsl(199 84% 30%)',padding:'2px 8px',borderRadius:'4px',fontSize:'0.7rem'}}>Extraído de PDF</span>}
                  <span style={{marginLeft:'8px',background: selectedType?.color + '15',color: selectedType?.color,padding:'2px 8px',borderRadius:'4px',fontSize:'0.7rem'}}>{selectedType?.label}</span>
                </p>
              </div>
            </div>
            <div style={{overflowX:'auto',maxHeight:'350px',overflowY:'auto'}}>
              <table className="data-table">
                <thead>
                  <tr>{uploadResult.columns?.map(c => <th key={c}>{c}</th>)}</tr>
                </thead>
                <tbody>
                  {uploadResult.preview?.slice(0, 15).map((row, i) => (
                    <tr key={i}>{uploadResult.columns?.map(c => <td key={c}>{row[c] != null ? String(row[c]) : '-'}</td>)}</tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div style={{display:'flex',gap:'8px',marginTop:'16px'}}>
            <button className="btn btn-secondary" onClick={() => { setStep(1); setUploadResult(null); }} data-testid="upload-wizard-back-button">
              <ArrowLeft size={14} /> Volver
            </button>
            <button className="btn btn-primary" onClick={handleProcess} disabled={loading} data-testid="upload-wizard-confirm-button" style={{height:'42px',minWidth:'200px'}}>
              {loading ? <><Loader2 size={14} /> Procesando...</> : <>Confirmar importación <CheckCircle size={14} /></>}
            </button>
          </div>
        </div>
      )}

      {/* Step 3: Results */}
      {step === 3 && processResult && (
        <div>
          <div className="section-card" style={{textAlign:'center'}}>
            <CheckCircle size={48} style={{color:'hsl(152,63%,33%)',marginBottom:'12px'}} />
            <h3>
              {processResult.file_type === 'inventario' ? 'Inventario actualizado' : 'Compras procesadas'}
            </h3>
            <p style={{fontSize:'0.82rem',color:'hsl(var(--muted-foreground))',marginTop:'4px'}}>
              {processResult.file_type === 'inventario' 
                ? 'El inventario se reemplazó con los nuevos datos.'
                : 'Las compras fueron homologadas contra tu catálogo maestro.'}
            </p>
            <div style={{display:'flex',gap:'24px',justifyContent:'center',marginTop:'16px'}}>
              <div>
                <div style={{fontSize:'1.5rem',fontWeight:700,fontFamily:'Space Grotesk',color:'hsl(152,63%,33%)'}}>{processResult.processed}</div>
                <div style={{fontSize:'0.75rem',color:'hsl(var(--muted-foreground))'}}>Procesados</div>
              </div>
              <div>
                <div style={{fontSize:'1.5rem',fontWeight:700,fontFamily:'Space Grotesk',color:'hsl(35,92%,45%)'}}>{processResult.pending_homologation}</div>
                <div style={{fontSize:'0.75rem',color:'hsl(var(--muted-foreground))'}}>Pendientes de validación</div>
              </div>
              <div>
                <div style={{fontSize:'1.5rem',fontWeight:700,fontFamily:'Space Grotesk',color:'hsl(0,72%,48%)'}}>{processResult.errors_count}</div>
                <div style={{fontSize:'0.75rem',color:'hsl(var(--muted-foreground))'}}>Errores</div>
              </div>
            </div>
          </div>
          {processResult.errors_count > 0 && (
            <div className="section-card">
              <h3>Errores encontrados</h3>
              {processResult.errors?.slice(0, 10).map((e, i) => (
                <div key={i} style={{fontSize:'0.8rem',padding:'6px 0',borderBottom:'1px solid hsl(var(--border)/0.3)'}}>
                  Fila {e.row}: {e.error}
                </div>
              ))}
            </div>
          )}
          <div style={{display:'flex',gap:'8px',marginTop:'16px'}}>
            <button className="btn btn-secondary" onClick={() => { setStep(1); setUploadResult(null); setProcessResult(null); }}>
              Cargar otro archivo
            </button>
            {processResult.pending_homologation > 0 && (
              <button className="btn btn-primary" onClick={() => window.location.href = '/homologation'}>
                Validar productos pendientes <ArrowRight size={14} />
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
